import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router';
import { Auth0Provider, AppState } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import './index.css';
import App from './app/App';
import { auth0Config } from './auth/authConfig';
import { appConfig } from './config/runtimeConfig';
import { BrandConfig } from './app/config/brand';
import { applyBrandDocument } from './app/config/brandDocument';
import { DevAuthProvider } from './auth/DevAuthProvider';
import { ErrorBoundary } from './shared/components/ErrorBoundary';

const isDevAuth = import.meta.env.VITE_AUTH_MODE === 'dev';

applyBrandDocument(BrandConfig);

const rumAppId = appConfig.ddRumApplicationId;
const rumClientToken = appConfig.ddRumClientToken;
if (rumAppId && rumClientToken) {
  datadogRum.init({
    applicationId: rumAppId,
    clientToken: rumClientToken,
    site: appConfig.ddSite || 'us5.datadoghq.com',
    service: appConfig.ddService || 'lds-efsp-portal',
    env: appConfig.ddEnv || 'dev',
    version: appConfig.ddVersion || 'local',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 5,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    plugins: [reactPlugin({ router: false })],
  });
}

async function enableMocking() {
  // Only enable mocking in development
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_MOCKING !== 'false') {
    const { worker } = await import('./mocks/browser');
    return worker.start({
      onUnhandledRequest: 'bypass', // Don't warn on unhandled requests (Auth0, etc.)
    });
  }
}

// Wrapper component to access React Router's navigate inside Auth0Provider
// eslint-disable-next-line react-refresh/only-export-components
const Auth0ProviderWithNavigate = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    // Navigate to the intended destination after login, or dashboard by default
    navigate(appState?.returnTo || '/dashboard');
  };

  return (
    <Auth0Provider
      {...auth0Config}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

enableMocking().then(() => {
  const AuthWrapper = isDevAuth ? DevAuthProvider : Auth0ProviderWithNavigate;

  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthWrapper>
            <App />
          </AuthWrapper>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
});
